import type { RequestLog } from '@mockd/shared';
import type { ActionExecutionResult } from '@mockd/shared/types/action-execution';

export interface MatchedRule {
  id: string;
  /** This rule produced the response; the others only ran actions. */
  capturedResponse: boolean;
  /** The rule's actions were skipped because the project is on the Free tier. */
  actionsSkipped: boolean;
}

function parseJsonArray<T>(json: string | null): T[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/** Every rule whose conditions matched the request, in evaluation order. */
export function parseMatchedRules(log: RequestLog): MatchedRule[] {
  let ids = parseJsonArray<string>(log.matched_rule_ids).filter((id) => typeof id === 'string');
  // Logs written before matched_rule_ids existed carry only the response rule
  if (ids.length === 0 && log.matched_response_rule_id) {
    ids = [log.matched_response_rule_id];
  }

  const skipped = new Set(
    parseJsonArray<ActionExecutionResult>(log.action_results)
      .filter((r) => r?.status === 'skipped' && typeof r.actionId === 'string')
      .map((r) => r.actionId.split(':action:')[0])
  );

  return ids.map((id) => ({
    id,
    capturedResponse: id === log.matched_response_rule_id,
    actionsSkipped: skipped.has(id),
  }));
}

/** A rule's current name, else the name logged with the response; null if the rule is gone. */
export function resolveRuleName(
  id: string,
  ruleNames: ReadonlyMap<string, string> | undefined,
  log: RequestLog
): string | null {
  return ruleNames?.get(id) ?? (id === log.matched_response_rule_id ? log.matched_response_rule_name : null) ?? null;
}
